<!-- src/views/DocLibrary.vue -->

<template>
  <div class="container coredeck-page">
    <h4>Document Library</h4>

    <!-- Controls -->
    <div class="row" style="margin-top: 10px">
      <div class="col s12 m8">
        <div class="input-field">
          <i class="material-icons prefix">search</i>

          <input
            id="fileSearch"
            v-model="search"
            type="text"
            placeholder="Search in this folder..."
          />
        </div>
      </div>

      <div
        class="col s12 m4"
        style="display: flex; align-items: center; gap: 10px; justify-content: flex-end"
      >
        <button class="btn blue-grey darken-3" type="button" @click="refresh">
          Refresh
        </button>

        <button
          class="btn grey darken-1"
          type="button"
          @click="upOne"
          :disabled="isAtRoot"
        >
          Up
        </button>
      </div>
    </div>

    <div v-if="loading" class="progress">
      <div class="indeterminate"></div>
    </div>

    <p v-else-if="error" class="red-text">
      {{ errorMessage }}
    </p>

    <template v-else>
      <p class="grey-text">
        Folder:
        <strong>
          {{ currentPath || rootPath }}
        </strong>
      </p>

      <!-- Folders -->
      <h6 style="margin-top: 18px">Folders</h6>

      <ul v-if="sortedFolders.length" class="collection">
        <li v-for="f in sortedFolders" :key="f.fullPath" class="collection-item">
          <a
            href="#!"
            @click.prevent="openPath(f.fullPath)"
            style="display: flex; align-items: center; gap: 10px"
          >
            <i class="material-icons"> folder </i>

            <span class="truncate">
              {{ f.name }}
            </span>
          </a>
        </li>
      </ul>

      <p v-else class="grey-text">No folders.</p>

      <!-- Files -->
      <h6 style="margin-top: 18px">Files</h6>

      <ul v-if="sortedFiles.length" class="collection">
        <li v-for="f in sortedFiles" :key="f.fullPath" class="collection-item">
          <div
            style="
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
            "
          >
            <div style="min-width: 0">
              <div class="truncate">
                <i
                  class="material-icons"
                  style="vertical-align: middle; margin-right: 6px"
                >
                  insert_drive_file
                </i>

                <strong>
                  {{ f.name }}
                </strong>
              </div>

              <div class="grey-text text-darken-1 truncate" style="font-size: 0.9rem">
                {{ f.fullPath }}

                <span v-if="f.contentType"> · {{ f.contentType }} </span>
              </div>
            </div>

            <a
              class="btn-small blue-grey darken-3"
              :href="f.url"
              target="_blank"
              rel="noreferrer"
            >
              Open
            </a>
          </div>
        </li>
      </ul>

      <p v-else class="grey-text">No files.</p>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";

import { useStorageFiles } from "@/scripts/useScripts/useStorageFiles";

/* --------------------------------------------------
   CONFIG
-------------------------------------------------- */

const rootPath = "doc-library";

/* --------------------------------------------------
   STATE
-------------------------------------------------- */

const search = ref("");

const { currentPath, loading, error, listFolder, filtered, goUp } = useStorageFiles();

/* --------------------------------------------------
   COMPUTED
-------------------------------------------------- */

const filteredSets = computed(() => filtered(search).value);

const filteredFolders = computed(() => filteredSets.value.folders);

const filteredFiles = computed(() => filteredSets.value.files);

const isAtRoot = computed(() => (currentPath.value || "") === rootPath);

const errorMessage = computed(() => {
  const code = error.value?.code;

  if (code === "storage/unauthorized") {
    return "You do not have permission to view this folder. Contact an admin.";
  }

  return error.value?.message || String(error.value || "");
});

/* --------------------------------------------------
   🔑 NUMERIC-AWARE SORT
-------------------------------------------------- */

function extractLeadingNumber(name) {
  if (!name) return null;

  const s = String(name).trim();
  const m = s.match(/^(\d+(?:\.\d+)?)/);

  return m ? Number(m[1]) : null;
}

function numericAwareSort(a, b) {
  const aNum = extractLeadingNumber(a?.name);
  const bNum = extractLeadingNumber(b?.name);

  const aIsNum = Number.isFinite(aNum);
  const bIsNum = Number.isFinite(bNum);

  if (aIsNum && bIsNum) {
    return aNum - bNum;
  }

  if (aIsNum && !bIsNum) {
    return -1;
  }

  if (!aIsNum && bIsNum) {
    return 1;
  }

  return String(a?.name ?? "").localeCompare(String(b?.name ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

const sortedFolders = computed(() => {
  return [...(filteredFolders.value || [])].sort(numericAwareSort);
});

const sortedFiles = computed(() => {
  return [...(filteredFiles.value || [])].sort(numericAwareSort);
});

/* --------------------------------------------------
   ACTIONS
-------------------------------------------------- */

const openPath = async (path) => {
  await listFolder(path);
};

const refresh = async () => {
  await listFolder(currentPath.value || rootPath);
};

const upOne = async () => {
  if (isAtRoot.value) return;

  const p = (currentPath.value || "").trim();

  if (!p || p === rootPath) return;

  await goUp();

  if (!currentPath.value || !currentPath.value.startsWith(rootPath)) {
    await listFolder(rootPath);
  }
};

/* --------------------------------------------------
   INIT
-------------------------------------------------- */

onMounted(async () => {
  await listFolder(rootPath);
});
</script>

<style scoped>
.breadcrumb-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.breadcrumb {
  color: #546e7a;
  font-weight: 600;
}

.breadcrumb-sep {
  color: rgba(0, 0, 0, 0.35);
}
</style>
